import { Link } from '@remix-run/react'
import { cn } from '#app/utils/misc.tsx'
import {
	PLAN_BLURBS,
	PLAN_ORDER,
	type PlanDetails,
	type SubscriptionPlan,
} from '#app/utils/plan-catalog.ts'
import {
	formatMonthlyAmount,
	formatYearlyAmount,
	planFeatureLines,
} from '#app/utils/plan-display.ts'
import { useOptionalUser } from '#app/utils/user.ts'
import { primaryCta, secondaryCta } from './shared.tsx'

type PlanCatalog = Record<SubscriptionPlan, PlanDetails>

export function PlanCards({
	plans,
	heading: Heading = 'h2',
}: {
	plans: PlanCatalog
	heading?: 'h2' | 'h3'
}) {
	const user = useOptionalUser()
	return (
		<ul className="mt-12 grid items-stretch gap-4 pt-3 md:grid-cols-3">
			{PLAN_ORDER.map((key) => {
				const plan = plans[key]
				const featured = key === 'pro'
				const yearly = formatYearlyAmount(plan.priceYearly)
				const href = user
					? key === 'free'
						? '/dashboard'
						: '/settings/profile/subscription'
					: '/signup'
				const label = user
					? key === 'free'
						? 'Go to your logbook'
						: `Review ${plan.name}`
					: key === 'free'
						? 'Create free account'
						: `Start free, upgrade to ${plan.name}`
				return (
					<li key={key} className={cn(featured && 'mt-3 md:mt-0')}>
						<article
							className={cn(
								'relative flex h-full flex-col rounded-[22px] border bg-[#0b1a36] p-7',
								featured
									? 'border-cyan-300/50 shadow-[0_30px_60px_-30px_rgba(45,212,191,0.45)]'
									: 'border-slate-400/15',
							)}
						>
							{featured ? (
								<p className="absolute -top-3 left-7 rounded-full bg-gradient-to-br from-cyan-300 to-teal-400 px-2.5 py-1 text-[0.72rem] font-bold uppercase tracking-wide text-[#021018]">
									For multi-tank keepers
								</p>
							) : null}
							<Heading className="text-xl font-semibold text-slate-50">
								{plan.name}
							</Heading>
							<p className="mt-1 text-slate-400">{PLAN_BLURBS[key]}</p>
							<div className="mt-4 h-28">
								<p className="text-[2.2rem] font-extrabold leading-none text-white">
									{formatMonthlyAmount(plan.priceMonthly)}
									{plan.priceMonthly > 0 ? (
										<small className="ml-1 text-base font-medium text-slate-400">
											/mo
										</small>
									) : null}
								</p>
								{yearly ? (
									<p className="mt-2 text-sm leading-5 text-slate-400">
										{yearly}
										<span className="mt-1 block">
											Yearly billing coming soon
										</span>
									</p>
								) : null}
							</div>
							<ul className="mb-6 mt-4 grid flex-1 gap-2.5">
								{planFeatureLines(plan).map((line) => (
									<li key={line} className="flex gap-2.5 text-slate-100">
										<span
											aria-hidden="true"
											className="font-extrabold text-teal-400"
										>
											✓
										</span>
										{line}
									</li>
								))}
							</ul>
							<Link
								to={href}
								className={cn(
									'mt-auto w-full',
									featured ? primaryCta : secondaryCta,
								)}
							>
								{label}
							</Link>
						</article>
					</li>
				)
			})}
		</ul>
	)
}
